/**
 * A question with its inline answer box. Shared by the employee drawer, the
 * question log page and the job detail panel.
 *
 * Two rendering branches:
 * - Structured rows (any of `question_context` / `recommendation` /
 *   `reasoning` set) lead with the bare question, show the employee's
 *   recommendation in a tinted block with a one-tap "Approve recommendation",
 *   and fold context + reasoning behind labeled toggles.
 * - Legacy single-blob rows render `question_text` through the line-count
 *   fold, exactly as before the structure existed.
 *
 * The meta line carries the drill-down: the asker links to their drawer, and
 * a question that parks a job links to that job. A pending question's age
 * escalates in tone as it grows stale — age is the urgency signal while an
 * answer is still owed.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";

import {
  type AiTeamAnswerBody,
  type AiTeamQuestion,
} from "../../api/admin";
import { useConfirm } from "../../components/confirm/context";
import { Markdown } from "../../components/Markdown";
import { Pill } from "../../components/Pill";
import { ShortId } from "../../components/ShortId";
import { FilterChips } from "../../components/ui/FilterChips";
import {
  QUESTION_FILTERS,
  type QuestionFilter,
  agentDrawerUrl,
  questionAgeTone,
  questionStatusTone,
  toneTextClass,
  urgencyTone,
} from "../../lib/aiTeam";
import { formatRelative } from "../../lib/formatRelative";
import { draftKey, useDraft } from "../../lib/hooks/useDraft";

export function QuestionStatusChips({
  value,
  onChange,
}: {
  value: QuestionFilter;
  onChange: (next: QuestionFilter) => void;
}) {
  return (
    <FilterChips
      aria-label="Question status"
      options={QUESTION_FILTERS}
      value={value}
      onChange={onChange}
    />
  );
}

export function QuestionCard({
  question,
  onAnswer,
  onDeny,
  showAsker = false,
}: {
  question: AiTeamQuestion;
  onAnswer: (body: AiTeamAnswerBody) => Promise<void>;
  onDeny?: () => void;
  showAsker?: boolean;
}) {
  // Keyed on the question id, so the draft follows the question across every
  // surface that renders this card (drawer tab, job detail, the full-page log)
  // and survives the unmounts none of them can prevent — a failed background
  // poll, the row leaving a filtered list, an accidental close, a reload.
  const { draft, setDraft, clearDraft, restored } = useDraft(
    draftKey("answer", question.id),
  );
  const [sending, setSending] = useState(false);
  const confirm = useConfirm();
  // Captured once per mount — hour-granular age tones don't need a ticking
  // clock, and calling Date.now() in render is impure under the compiler.
  const [nowMs] = useState(() => Date.now());
  const pending = question.status === "pending";
  const structured = !!(
    question.question_context ||
    question.recommendation ||
    question.reasoning
  );

  // Clear only once the answer is actually in — a failed send must leave the
  // text where the operator can retry it, not swallow what they typed.
  const submit = async (answer: string) => {
    const text = answer.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await onAnswer({ answer: text });
      clearDraft();
    } catch {
      // The host surfaces the error via its toast; the draft stays put.
    } finally {
      setSending(false);
    }
  };

  const approveRecommendation = async () => {
    if (sending) return;
    setSending(true);
    try {
      await onAnswer({ accept_recommendation: true });
      clearDraft();
    } catch {
      // Toast lives on the host; a half-typed override draft stays put.
    } finally {
      setSending(false);
    }
  };

  const askerLink =
    question.requested_by_user_id && question.requested_by_slug ? (
      <Link
        className="link-quiet font-mono"
        to={agentDrawerUrl(question.requested_by_user_id, {
          tab: "questions",
        })}
      >
        {question.requested_by_slug}
      </Link>
    ) : (
      <>{question.requested_by_slug || "—"}</>
    );

  return (
    <div className="panel">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 text-xs text-ink-low">
          {showAsker ? <>from {askerLink} · </> : null}
          to {question.addressed_to_slug || question.inbox_email} ·{" "}
          {pending ? (
            <span
              className={clsx("font-mono", toneTextClass(questionAgeTone(question.created_at, nowMs)))}
              title={question.created_at ?? undefined}
            >
              {formatRelative(question.created_at) ?? "—"}
            </span>
          ) : (
            formatRelative(question.created_at)
          )}
          {" · "}
          <ShortId id={question.id} />
          {question.escalated_at ? " · escalated" : null}
          {question.parent_confirmation_id ? " (escalation)" : null}
          {question.job_id && question.requested_by_user_id ? (
            <>
              {" · "}
              <Link
                className="link"
                to={agentDrawerUrl(question.requested_by_user_id, {
                  tab: "jobs",
                  jobId: question.job_id,
                })}
              >
                blocks a job →
              </Link>
            </>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Pill value={question.urgency} tone={urgencyTone(question.urgency)} />
          <Pill
            value={question.status}
            tone={questionStatusTone(question.status)}
          />
        </div>
      </div>

      {structured ? (
        // The bare question is never folded while pending — you cannot answer
        // what you cannot read. Resolved rows fold everything: the log is
        // scanned, not re-read.
        <div className="mb-2">
          {pending ? (
            <Markdown text={question.question_text ?? ""} />
          ) : (
            <CollapsibleBlock text={question.question_text} />
          )}
          {question.recommendation ? (
            pending ? (
              <div className="question-reco">
                <div className="micro-label">Recommended</div>
                <Markdown text={question.recommendation} />
                <button
                  type="button"
                  className="btn btn-main btn-sm mt-2"
                  disabled={sending}
                  onClick={() => void approveRecommendation()}
                >
                  {sending ? "Sending…" : "Approve recommendation"}
                </button>
              </div>
            ) : (
              <FoldSection label="Recommendation" text={question.recommendation} />
            )
          ) : null}
          <FoldSection label="Context" text={question.question_context} />
          <FoldSection label="Reasoning" text={question.reasoning} />
        </div>
      ) : (
        /* Open questions start expanded — you cannot answer what you cannot
           read. Resolved ones fold: the log is scanned, not re-read. */
        <div className="mb-2">
          <CollapsibleBlock
            text={question.question_text}
            defaultOpen={pending}
          />
        </div>
      )}

      {pending ? (
        <div className="space-y-2">
          {question.options?.length ? (
            <div className="flex flex-wrap gap-2">
              {question.options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className="btn btn-second btn-sm"
                  disabled={sending}
                  onClick={() => void submit(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          ) : null}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="label" htmlFor={`answer-${question.id}`}>
                Answer
                {restored ? (
                  <span className="ml-2 normal-case tracking-normal text-ink-low">
                    · restored unsent draft
                  </span>
                ) : null}
              </label>
              {/* A structured question gets a structured answer — six
                  numbered decisions don't fit in two rows. */}
              <textarea
                id={`answer-${question.id}`}
                className="input"
                rows={6}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                style={{ resize: "vertical" }}
              />
            </div>
            <button
              type="button"
              className="btn btn-main"
              disabled={!draft.trim() || sending}
              onClick={() => void submit(draft)}
            >
              {sending ? "Sending…" : "Send"}
            </button>
            {onDeny ? (
              <button
                type="button"
                className="btn btn-ghost btn-danger"
                onClick={async () => {
                  const ok = await confirm({
                    title: "Decline to answer?",
                    body: "The asker is unblocked with a denial — it will proceed without the answer it wanted.",
                    confirmLabel: "Deny",
                    tone: "danger",
                  });
                  if (ok) onDeny();
                }}
              >
                Deny
              </button>
            ) : null}
          </div>
        </div>
      ) : question.answer_text ? (
        <div className="border-t border-rule pt-2">
          <div className="text-xs uppercase tracking-wider text-ink-low mb-1">
            Answer
            {question.answered_via ? (
              <span className="ml-2 normal-case tracking-normal">
                · via {question.answered_via}
              </span>
            ) : null}
          </div>
          <CollapsibleBlock text={question.answer_text} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * A labeled fold for the structured sections (context, reasoning). Always
 * starts collapsed regardless of length — unlike `CollapsibleBlock`, whose
 * fold is line-count-measured: the structure already says these are
 * supporting material, so they earn their screen space only on demand.
 */
function FoldSection({
  label,
  text,
}: {
  label: string;
  text?: string | null;
}) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <div className="mt-2">
      <button
        type="button"
        className="text-xs uppercase tracking-wider text-ink-low hover:text-ink-medium"
        onClick={() => setOpen((v) => !v)}
      >
        {label} {open ? "▴" : "▸"}
      </button>
      {open ? (
        <div className="mt-1">
          <Markdown text={text} />
        </div>
      ) : null}
    </div>
  );
}

/** Source lines shown before a long block collapses behind a toggle. */
const COLLAPSE_AFTER_LINES = 14;

/**
 * Agent-authored markdown that is long enough to need a fold.
 *
 * Rendering goes through the shared `Markdown` component (react-markdown, no
 * raw HTML — see `components/Markdown.tsx` for why that boundary matters).
 * The fold is measured on *source* lines and exists because a chief of staff
 * writes questions in the hundreds of lines: a whole page of them is
 * unscannable when every one is fully open, and a plain clamp hides that
 * there is more to read.
 */
export function CollapsibleBlock({
  text,
  defaultOpen = false,
}: {
  text?: string | null;
  defaultOpen?: boolean;
}) {
  const lines = (text ?? "").split("\n");
  const foldable = lines.length > COLLAPSE_AFTER_LINES;
  const [open, setOpen] = useState(defaultOpen || !foldable);

  if (!text) return <span className="text-sm text-ink-low">—</span>;
  const shown = open ? text : lines.slice(0, COLLAPSE_AFTER_LINES).join("\n");

  return (
    <div>
      <Markdown text={shown} />
      {foldable ? (
        <button
          type="button"
          className="btn btn-ghost btn-sm mt-1"
          onClick={() => setOpen((v) => !v)}
        >
          {open
            ? "Show less"
            : `Show all ${lines.length} lines`}
        </button>
      ) : null}
    </div>
  );
}
